/**
 * The building's month, for the people who have to answer for it.
 *
 * The accountant works a unit at a time. A committee member wants one number
 * they can repeat at a meeting, and then enough behind it to know which block
 * to worry about. Everything here is arithmetic over the projection the
 * accountant's map already reads, so the two can never disagree about how much
 * came in.
 *
 * A rate is verified over billed. Money still waiting on the accountant is not
 * collected, because until somebody confirms it arrived the only true thing to
 * say is that it is waiting.
 */

import type { PeriodSummary } from "./ledger.js";
import type { Birr } from "./money.js";
import { type Period, comparePeriods } from "./period.js";
import { compareUnitIds } from "./units.js";

export interface MonthTotals {
  period: Period;
  billed: Birr;
  collected: Birr;
  outstanding: Birr;
  /** Collected over billed, 0 to 1. A month with nothing billed is fully collected. */
  rate: number;
}

export interface BlockTotals extends MonthTotals {
  block: string;
  units: number;
}

export interface Overview {
  month: MonthTotals;
  /** What the building spent this month, cancelled ones left out. */
  spent: Birr;
  /** What the accounts hold, as somebody last counted them. */
  held: Birr;
  /** What came in this month less what went out. Negative is a month at a loss. */
  net: Birr;
  /** Worst collecting block first, because that is the one to ask about. */
  blocks: BlockTotals[];
  /** Oldest month first, for reading left to right. */
  trend: MonthTotals[];
  /** Everything owing across every month, not only the one on screen. */
  arrears: Birr;
}

interface Running {
  billed: Birr;
  collected: Birr;
  outstanding: Birr;
  units: number;
}

const nothing = (): Running => ({ billed: 0, collected: 0, outstanding: 0, units: 0 });

function add(running: Running, summary: PeriodSummary): void {
  running.billed += summary.charged;
  running.collected += summary.credited;
  running.outstanding += summary.balance;
  running.units += 1;
}

function totalsFor(period: Period, running: Running): MonthTotals {
  return {
    period,
    billed: running.billed,
    collected: running.collected,
    outstanding: running.outstanding,
    rate: running.billed === 0 ? 1 : running.collected / running.billed,
  };
}

export function overview(options: {
  /** One row per unit per month, over however many months the trend covers. */
  months: readonly { unitId: string; summary: PeriodSummary }[];
  blocks: ReadonlyMap<string, string>;
  period: Period;
  arrears: Birr;
  /** This month's expenses, already filtered to the month and to what stands. */
  spent?: Birr;
  held?: Birr;
}): Overview {
  const { months, blocks, period, arrears, spent = 0, held = 0 } = options;

  const byPeriod = new Map<Period, Running>();
  const byBlock = new Map<string, Running>();

  for (const row of months) {
    const forPeriod = byPeriod.get(row.summary.period) ?? nothing();
    add(forPeriod, row.summary);
    byPeriod.set(row.summary.period, forPeriod);

    if (row.summary.period !== period) {
      continue;
    }
    const block = blocks.get(row.unitId);
    if (block === undefined) {
      continue;
    }
    const forBlock = byBlock.get(block) ?? nothing();
    add(forBlock, row.summary);
    byBlock.set(block, forBlock);
  }

  const month = totalsFor(period, byPeriod.get(period) ?? nothing());

  return {
    month,
    spent,
    held,
    net: month.collected - spent,
    blocks: [...byBlock.entries()]
      .map(([block, running]) => ({
        block,
        units: running.units,
        ...totalsFor(period, running),
      }))
      // Worst first, and by door where two blocks collect the same, so the
      // ranking does not reshuffle itself every time a payment lands.
      .sort((one, two) => one.rate - two.rate || compareUnitIds(one.block, two.block)),
    trend: [...byPeriod.entries()]
      .map(([month, running]) => totalsFor(month, running))
      .sort((one, two) => comparePeriods(one.period, two.period)),
    arrears,
  };
}
